import { useMutation } from '@apollo/client';
import type { VariablesOf } from '@graphql-typed-document-node/core';
import React from 'react';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import UPDATE_TOPIC_MUTATION from '../../graphql/updateTopicMutation';
import { UIActions } from '../../ui/uiSlice';
import useNavigate from '../../utils/router/useNavigate';
import topicPath from '../../utils/url/topicPath';
import useDeleteTopic from '../useDeleteTopic';
import TopicModal from './TopicModal';

const TOPIC_FRAGMENT = gql(`
  fragment EditTopicModal_topic on Topic {
    id
    name
    parent {
      id
    }
    privacy
    memberEditable
    detached
  }
`);

interface EditTopicModalProps {
  close(): void;
  topic: DocumentType<typeof TOPIC_FRAGMENT>;
}

const EditTopicModal = ({
  close,
  topic: { id, name, parent, privacy, memberEditable, detached },
}: EditTopicModalProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const parentId = parent?.id;
  const [updateTopic] = useMutation(UPDATE_TOPIC_MUTATION);
  const deleteTopic = useDeleteTopic(id);

  const onSubmit = (
    values: { name: string } & Omit<
      VariablesOf<typeof UPDATE_TOPIC_MUTATION>,
      'id'
    >,
  ) =>
    updateTopic({
      variables: {
        id,
        ...values,
        privacy: privacy !== values.privacy ? values.privacy : undefined,
        description: JSON.stringify(values.description),
      },
    })
      .then(() => dispatch(UIActions.closeModal({ reset: true })))
      .then(() => navigate(topicPath({ id, name: values.name })));

  return (
    <TopicModal
      close={close}
      id={id}
      name={name}
      onDelete={deleteTopic}
      onSubmit={onSubmit}
      parentId={parentId}
      privacy={privacy}
      memberEditable={memberEditable}
      detached={detached}
      submitText="Save"
      title="Edit Topic"
    />
  );
};

export default EditTopicModal;
